import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Label,
  ButtonGroup,
  Col,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";

// Listo para Surtir: articulos con pendiente de surtir (por defecto solo
// autorizados, de pedidos abiertos y activos) y la existencia en cada bodega.
// Las tiendas no se consideran: su mercancia no se usa para surtir pedidos.
function ListoSurtir() {
  const { user } = useContext(AuthContext);
  const URL_ARTICULOS_PEDIDO = process.env.REACT_APP_URL_ARTICULOS_PEDIDO;

  const [articulosPedidos, setArticulosPedidos] = useState([]);
  const [articuloDetalle, setArticuloDetalle] = useState("");
  const [modalPedidos, setModalPedidos] = useState(false);
  const togglePedidos = () => setModalPedidos(!modalPedidos);

  const [bodegas, setBodegas] = useState([]);
  const [autorizados, setAutorizados] = useState("Si");
  const [existencia, setExistencia] = useState("Con");

  //--- Pagination
  const [comments, setComments] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 100;

  useEffect(() => {
    showLoader();
    axios
      .get(`${URL_ARTICULOS_PEDIDO}ListoSurtirBodegas?autorizados=${autorizados}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setBodegas(response.data.bodegas || []);
        setComments(response.data.filas || []);
        setCurrentPage(1);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autorizados]);

  const fmt = (n) => new Intl.NumberFormat("en-US").format(n || 0);
  const enBodega = (fila, bodega) => (fila.porBodega && fila.porBodega[bodega]) || 0;

  // Misma fuente para la tabla, el PDF y el Excel
  const datosFiltrados = useMemo(() => {
    let data = comments;
    if (existencia === "Con") data = data.filter((c) => c.disponible > 0);
    if (existencia === "Sin") data = data.filter((c) => c.disponible <= 0);
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (c) =>
          (c.codigo || "").toLowerCase().includes(q) ||
          (c.nombre || "").toLowerCase().includes(q)
      );
    }
    if (sorting.field) {
      const dir = sorting.order === "asc" ? 1 : -1;
      const valor = (f) =>
        sorting.field.startsWith("bod:") ? enBodega(f, sorting.field.slice(4)) : f[sorting.field];
      data = [...data].sort((a, b) => {
        const va = valor(a);
        const vb = valor(b);
        if (typeof va === "number" || typeof vb === "number") return dir * ((va || 0) - (vb || 0));
        return dir * String(va || "").localeCompare(String(vb || ""));
      });
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, existencia, search, sorting]);

  const commentsData = useMemo(() => {
    setTotalItems(datosFiltrados.length);
    return datosFiltrados.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosFiltrados, currentPage]);

  // Totales de todo lo filtrado (no solo de la pagina visible)
  const totales = useMemo(() => {
    const t = { cantPed: 0, pendSurtir: 0, disponible: 0, falta: 0, bodegas: {} };
    for (const f of datosFiltrados) {
      t.cantPed += f.cantPed;
      t.pendSurtir += f.pendSurtir;
      t.disponible += f.disponible;
      t.falta += f.falta;
      for (const b of bodegas) t.bodegas[b] = (t.bodegas[b] || 0) + enBodega(f, b);
    }
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosFiltrados, bodegas]);

  const headers = [
    { name: "Codigo", field: "codigo", sortable: true },
    { name: "Articulo", field: "nombre", sortable: true },
    { name: "Pzs Caja", field: "piezasPorCaja", sortable: true },
    { name: "Pedidos", field: "numPedidos", sortable: true },
    { name: "Total Pedido", field: "cantPed", sortable: true },
    { name: "Pend Surtir", field: "pendSurtir", sortable: true },
    ...bodegas.map((b) => ({ name: b, field: `bod:${b}`, sortable: true })),
    { name: "Disponible", field: "disponible", sortable: true },
    { name: "Falta", field: "falta", sortable: true },
    { name: "Detalle", field: "detalle", sortable: false },
  ];

  const titulo = `Listo para Surtir${autorizados === "Si" ? " (autorizados)" : ""}`;

  function PDFTabla() {
    const doc = new jsPDF("landscape", "mm", "a4", true);
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 250, 8, 30, 25);
    doc.text(titulo, 15, 25);
    doc.autoTable({
      head: [["Codigo", "Articulo", "Pedidos", "Total Pedido", "Pend Surtir", ...bodegas, "Disponible", "Falta"]],
      body: datosFiltrados.map((f) => [
        f.codigo,
        f.nombre,
        f.numPedidos,
        fmt(f.cantPed),
        fmt(f.pendSurtir),
        ...bodegas.map((b) => (enBodega(f, b) ? fmt(enBodega(f, b)) : "")),
        fmt(f.disponible),
        f.falta ? fmt(f.falta) : "",
      ]),
      foot: [[
        "TOTALES", "", "",
        fmt(totales.cantPed),
        fmt(totales.pendSurtir),
        ...bodegas.map((b) => fmt(totales.bodegas[b])),
        fmt(totales.disponible),
        fmt(totales.falta),
      ]],
      styles: { fontSize: 7 },
      headStyles: { fillColor: ["#f28029"], lineWidth: 0 },
      footStyles: { fillColor: ["#f28029"] },
      theme: "grid",
      startY: 35,
      showFoot: "lastPage",
    });
    doc.save("ListoParaSurtir.pdf");
  }

  function excel() {
    const columnas = ["Codigo", "Articulo", "PzsCaja", "Pedidos", "TotalPedido", "PendSurtir", ...bodegas, "Disponible", "Falta"];
    const datos = datosFiltrados.map((f) => {
      const fila = {
        Codigo: f.codigo,
        Articulo: f.nombre,
        PzsCaja: f.piezasPorCaja,
        Pedidos: f.numPedidos,
        TotalPedido: f.cantPed,
        PendSurtir: f.pendSurtir,
        Disponible: f.disponible,
        Falta: f.falta,
      };
      for (const b of bodegas) fila[b] = enBodega(f, b);
      return fila;
    });
    const toExcel = new ExportJsonExcel({
      fileName: "ListoParaSurtir",
      datas: [{ sheetData: datos, sheetName: "ListoParaSurtir", sheetFilter: columnas, sheetHeader: columnas }],
    });
    toExcel.saveExcel();
  }

  function jalaPedidos(fila) {
    setArticulosPedidos([]);
    setArticuloDetalle(`${fila.codigo} ${fila.nombre}`);
    axios
      .get(`${URL_ARTICULOS_PEDIDO}Articulo/${fila.idArticulo}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        // Mismo criterio que el reporte: pedidos abiertos y activos,
        // y si el filtro lo pide, solo lo autorizado
        const renglones = response.data.filter((a) => {
          const ped = a.pedidos && a.pedidos[0];
          if (!ped || a.is_active !== "Si" || !(a.pendiente_surtir > 0)) return false;
          if (ped.is_active !== "Si" || ped.status !== "Abierto") return false;
          if (autorizados === "Si" && (a.autorizadoSurtido !== "Si" || ped.autorizadoSurtido !== "Si")) return false;
          return true;
        });
        setArticulosPedidos(renglones);
      })
      .catch((err) => console.log(err));
    togglePedidos();
  }

  let detCantidad = 0;
  let detSurtido = 0;
  let detPendiente = 0;

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_surtidos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuVentas" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">{titulo}</h3>
          <p align="center" style={{ color: "#64748b", marginTop: -6 }}>
            Pendiente de surtir de pedidos abiertos, con la existencia en cada bodega (sin tiendas)
          </p>

          <Row>
            <Col md={3}>
              <Label>Pendiente</Label>
              <Input
                bsSize="sm"
                type="select"
                value={autorizados}
                onChange={(e) => setAutorizados(e.target.value)}
              >
                <option value="Si">Solo autorizados</option>
                <option value="No">Todos</option>
              </Input>
            </Col>
            <Col md={3}>
              <Label>Existencia</Label>
              <Input
                bsSize="sm"
                type="select"
                value={existencia}
                onChange={(e) => {
                  setExistencia(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="Con">Con existencia en bodega</option>
                <option value="Sin">Sin existencia</option>
                <option value="">Todos</option>
              </Input>
            </Col>
          </Row>
          <br />

          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader headers={headers} onSorting={(field, order) => setSorting({ field, order })} />
            <tbody>
              {commentsData.map((a) => (
                <tr key={a.idArticulo}>
                  <td>{a.codigo}</td>
                  <td>{a.nombre}</td>
                  <td>{a.piezasPorCaja}</td>
                  <td>{a.numPedidos}</td>
                  <td>{fmt(a.cantPed)}</td>
                  <td>{fmt(a.pendSurtir)}</td>
                  {bodegas.map((b) => (
                    <td key={b}>{enBodega(a, b) ? fmt(enBodega(a, b)) : ""}</td>
                  ))}
                  <td className="negrita">{fmt(a.disponible)}</td>
                  <td style={a.falta > 0 ? { color: "#dc3545", fontWeight: 700 } : undefined}>
                    {a.falta > 0 ? fmt(a.falta) : ""}
                  </td>
                  <td>
                    <Button color="info" size="sm" title="Ver pedidos" onClick={() => jalaPedidos(a)}>
                      <i class="fas fa-search"></i>
                    </Button>
                  </td>
                </tr>
              ))}

              <tr>
                <td className="negrita">TOTALES</td>
                <td></td>
                <td></td>
                <td></td>
                <td className="negrita">{fmt(totales.cantPed)}</td>
                <td className="negrita">{fmt(totales.pendSurtir)}</td>
                {bodegas.map((b) => (
                  <td key={b} className="negrita">{fmt(totales.bodegas[b])}</td>
                ))}
                <td className="negrita">{fmt(totales.disponible)}</td>
                <td className="negrita" style={{ color: "#dc3545" }}>{fmt(totales.falta)}</td>
                <td></td>
              </tr>
            </tbody>
          </Table>
          <div className="col-md-6">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : undefined}

      <Modal size="xl" isOpen={modalPedidos} toggle={togglePedidos}>
        <ModalHeader toggle={togglePedidos}>
          <h4>Pedidos pendientes</h4>
          <h5>{articuloDetalle}</h5>
        </ModalHeader>
        <ModalBody>
          <Table size="sm" striped borderless className="table-responsive-xl">
            <tbody>
              <tr>
                <th>Pedido</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Aut Surtir</th>
                <th>Cantidad</th>
                <th>Surtido</th>
                <th>Pend Surtir</th>
              </tr>
              {articulosPedidos.map((a) => {
                detCantidad += parseFloat(a.cantidad) || 0;
                detSurtido += parseFloat(a.surtido) || 0;
                detPendiente += parseFloat(a.pendiente_surtir) || 0;
                return (
                  <tr key={a._id}>
                    <td>{a.pedidos[0].idPedido}</td>
                    <td>{a.pedidos[0].fecha}</td>
                    <td>{a.pedidos[0].clientes[0].nombre_comercial}</td>
                    <td>{a.autorizadoSurtido}</td>
                    <td>{fmt(a.cantidad)}</td>
                    <td>{fmt(a.surtido)}</td>
                    <td>{fmt(a.pendiente_surtir)}</td>
                  </tr>
                );
              })}
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td className="negrita">TOTALES</td>
                <td className="negrita">{fmt(detCantidad)}</td>
                <td className="negrita">{fmt(detSurtido)}</td>
                <td className="negrita">{fmt(detPendiente)}</td>
              </tr>
            </tbody>
          </Table>
        </ModalBody>
      </Modal>

      {loader}
    </>
  );
}

export default ListoSurtir;
